/**
 * Bulk match orchestrator — rank N candidates against one JD in parallel.
 *
 * Design (Day 5 + Day 9 two-stage retrieval):
 *   1. Per-candidate engineMatch runs with useReranker=false (bi-encoder only,
 *      fast) under a concurrency-limited semaphore.
 *   2. After every candidate is scored, `rerankTopSlots` reruns the
 *      cross-encoder on the top N candidates (default 5) and blends the
 *      cross-encoder score into their overallScore. This reorders the top
 *      of the ranking based on the best pairwise relevance estimate we
 *      have while keeping overall cost bounded.
 *
 * The JD is parsed once and reused across all candidates — the engine's
 * embedding cache then hits for every per-candidate match. The cross-encoder
 * sees each (JD, resume) pair as a joint input so it can't be cached the
 * same way; it runs sequentially on top N only.
 */

import {
  MsMarcoReranker,
  blendWithCrossEncoder,
  verdictFromScore,
  type Reranker,
} from "@talent-align/engine";

import type { EngineMatchResult } from "./match-engine";
import { engineMatch } from "./match-engine";
import type { CompleteOptions } from "@/hooks/useOpenRouter";

export type CandidateStatus =
  | "queued"
  | "scoring"
  | "reranking"
  | "done"
  | "error"
  | "cancelled";

export interface CandidateSlot {
  id: string;
  sourceName: string;
  rawText: string;
  status: CandidateStatus;
  result?: EngineMatchResult;
  error?: string;
  startedAt?: number;
  finishedAt?: number;
  /** Day 9: 1-based original rank (by bi-encoder score, before rerank).
   *  Set after the bulk run completes, never set during streaming. */
  originalRank?: number;
  /** Day 9: whether this candidate's final score was influenced by the
   *  cross-encoder rerank pass. `false` for candidates outside the top-N. */
  reranked?: boolean;
}

export interface BulkProgressStats {
  total: number;
  queued: number;
  scoring: number;
  done: number;
  errors: number;
  completedCount: number;
}

export function computeStats(slots: CandidateSlot[]): BulkProgressStats {
  let queued = 0;
  let scoring = 0;
  let done = 0;
  let errors = 0;
  for (const s of slots) {
    if (s.status === "queued") queued++;
    else if (s.status === "scoring" || s.status === "reranking") scoring++;
    else if (s.status === "done") done++;
    else if (s.status === "error") errors++;
  }
  return {
    total: slots.length,
    queued,
    scoring,
    done,
    errors,
    completedCount: done + errors,
  };
}

type CompleteFn = (opts: CompleteOptions) => Promise<string>;

export interface BulkMatchOptions {
  concurrency?: number;
  model?: string;
  signal?: AbortSignal;
  /** Day 9: number of top candidates to rerank with the cross-encoder
   *  after the bi-encoder pass. Default 5. Set to 0 to disable rerank. */
  rerankTopN?: number;
  onUpdate?: (slots: readonly CandidateSlot[]) => void;
}

// Shared reranker singleton — the model downloads once per page session.
let sharedReranker: Reranker | null = null;
function getReranker(): Reranker {
  if (!sharedReranker) sharedReranker = new MsMarcoReranker();
  return sharedReranker;
}

/**
 * Run the matching engine across every candidate slot against a single JD,
 * then cross-encoder-rerank the top N.
 *
 * The returned promise resolves when every slot is in a terminal state
 * (done, error, or cancelled). Callers can still observe progress live via
 * the `onUpdate` callback during execution.
 */
export async function runBulkMatch(
  complete: CompleteFn,
  jdText: string,
  initialCandidates: Array<{ id: string; sourceName: string; rawText: string }>,
  opts: BulkMatchOptions = {},
): Promise<CandidateSlot[]> {
  const concurrency = Math.max(1, Math.min(opts.concurrency ?? 3, 8));
  const model = opts.model ?? "llama-3.3-70b-versatile";
  const rerankTopN = Math.max(0, opts.rerankTopN ?? 5);
  const signal = opts.signal;

  // Build the mutable slot array. A shared reference so the semaphore workers
  // and the emitter see the same state.
  const slots: CandidateSlot[] = initialCandidates.map((c) => ({
    id: c.id,
    sourceName: c.sourceName,
    rawText: c.rawText,
    status: "queued",
  }));

  function emit(): void {
    opts.onUpdate?.(slots.slice());
  }

  // Initial paint so the UI shows the queue before any work starts
  emit();

  // Worker-pool pattern: cursor walks the queue; each worker grabs the next
  // index, runs the match, updates the slot in place, then loops.
  let cursor = 0;

  async function worker(): Promise<void> {
    while (true) {
      if (signal?.aborted) return;
      const idx = cursor++;
      if (idx >= slots.length) return;
      const slot = slots[idx];
      if (!slot) return;

      slot.status = "scoring";
      slot.startedAt = performance.now();
      emit();

      try {
        // Bi-encoder only on the first pass. Cross-encoder reruns on
        // the top N below, after every candidate has been scored.
        const result = await engineMatch(complete, slot.rawText, jdText, model, {
          useReranker: false,
        });
        if (signal?.aborted) {
          slot.status = "cancelled";
          slot.finishedAt = performance.now();
          emit();
          return;
        }
        slot.result = result;
        slot.status = "done";
        slot.finishedAt = performance.now();
      } catch (err) {
        slot.status = "error";
        slot.error = err instanceof Error ? err.message : "Unknown error";
        slot.finishedAt = performance.now();
      }
      emit();
    }
  }

  // Kick off N workers and wait for all of them
  const workers: Promise<void>[] = [];
  for (let i = 0; i < concurrency; i++) {
    workers.push(worker());
  }
  await Promise.all(workers);

  if (signal?.aborted) return slots;

  // -------------------------------------------------------------------
  // Second stage: cross-encoder rerank of the top N.
  //
  // Record the original rank every done slot had under pure bi-encoder
  // scoring so the UI can show position deltas. Then take the top N,
  // flip their status to "reranking" so the progress card can communicate
  // what's happening, score them through the cross-encoder one at a time,
  // blend into the final overallScore, and flip status back to "done".
  // -------------------------------------------------------------------
  if (rerankTopN > 0) {
    const doneSlots = slots
      .filter((s): s is CandidateSlot & { result: EngineMatchResult } =>
        s.status === "done" && !!s.result,
      )
      .sort((a, b) => b.result.match.overallScore - a.result.match.overallScore);

    // Annotate every slot with its original (bi-encoder) rank
    doneSlots.forEach((s, i) => {
      s.originalRank = i + 1;
    });

    const topSlots = doneSlots.slice(0, rerankTopN);
    if (topSlots.length > 0) {
      const reranker = getReranker();
      for (const slot of topSlots) {
        if (signal?.aborted) break;
        slot.status = "reranking";
        emit();
        try {
          const crossScore = await reranker.score(jdText, slot.rawText);
          const biencoderScore = slot.result.match.overallScore;
          const blended = blendWithCrossEncoder(biencoderScore, crossScore, 0.4);
          slot.result.match.overallScore = blended;
          slot.result.match.verdict = verdictFromScore(blended);
          slot.result.match.crossEncoderScore = crossScore;
          slot.result.match.biencoderScore = biencoderScore;
          slot.result.match.modelUsed =
            (slot.result.match.modelUsed ?? "") + "+msmarco-ce";
          slot.reranked = true;
        } catch (err) {
          // Rerank failure is non-fatal — keep the bi-encoder result
          console.warn(
            `[bulk-match] reranker failed for ${slot.sourceName}, keeping bi-encoder score`,
            err,
          );
        }
        slot.status = "done";
        emit();
      }
    }
  }

  return slots;
}
